import apiSlice from "./api/api";
import { BRANCHES, DEPARTMENTS, DESIGNATIONS, LEAVE_TYPES, TERMINATION_TYPES, COUNTRIES, STATES, DOCUMENT_TYPES, BANKS, HOLIDAYS, ROLES, PERMISSIONS, COMPANY_SETTINGS, APPROVAL_SETTINGS, EMAIL_SETTINGS, EMAIL_TEST, LOCALIZATION_SETTINGS, SALARY_SETTINGS, NOTIFICATION_SETTINGS, SMS_SETTINGS, SMS_TEST } from "./ENDPOINTS";

const settingsApiSlice = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        // get branches
        getBranches: builder.query({
            query: () => ({
                url: BRANCHES,
                method: "GET",
            }),
            providesTags: ["Branches"],
        }),
        // save branches
        saveBranches: builder.mutation({
            query: (data) => ({
                url: BRANCHES,
                method: "POST",
                body: data
            }),
            invalidatesTags: ["Branches"],
        }),
        // get leave types
        getLeaveTypes: builder.query({
            query: () => ({
                url: LEAVE_TYPES,
                method: "GET",
            }),
            providesTags: ["LeaveTypes"],
        }),
        // fetch leave types
        saveLeaveTypes: builder.mutation({
            query: (data) => ({
                url: LEAVE_TYPES,
                method: "POST",
                body: data
            }),
            invalidatesTags: ["LeaveTypes"],
        }),
        // get departments
        getDepartments: builder.query({
            query: () => ({
                url: DEPARTMENTS,
                method: "GET",
            }),
            providesTags: ["Departments"],
        }),
        // save departments
        saveDepartments: builder.mutation({
            query: (data) => ({
                url: DEPARTMENTS,
                method: "POST",
                body: data
            }),
            invalidatesTags: ["Departments"],
        }),
        // get designations
        getDesignations: builder.query({
            query: () => ({
                url: DESIGNATIONS,
                method: "GET",
            }),
            providesTags: ["Designations"],
        }),
        // save designations
        saveDesignations: builder.mutation({
            query: (data) => ({
                url: DESIGNATIONS,
                method: "POST",
                body: data
            }),
            invalidatesTags: ["Designations"],
        }),
        // get termination types
        getTerminationTypes: builder.query({
            query: () => ({
                url: TERMINATION_TYPES,
                method: "GET",
            }),
            providesTags: ["TerminationTypes"],
        }),
        // save termination types
        saveTerminationType: builder.mutation({
            query: (data) => ({
                url: TERMINATION_TYPES,
                method: "POST",
                body: data
            }),
            invalidatesTags: ["TerminationTypes"],
        }),
        // get countries
        getCountries: builder.query({
            query: (activeOnly = true) => ({
                url: `${COUNTRIES}?activeOnly=${activeOnly}`,
                method: "GET",
            }),
            providesTags: ["Countries"],
        }),
        // get states
        getStates: builder.query({
            query: (countryId) => ({
                url: countryId ? `${STATES}?countryId=${countryId}&activeOnly=true` : `${STATES}?activeOnly=true`,
                method: "GET",
            }),
            providesTags: ["States"],
        }),
        // get document types
        getDocumentTypes: builder.query({
            query: () => ({
                url: DOCUMENT_TYPES,
                method: "GET",
            }),
            providesTags: ["DocumentTypes"],
        }),
        // save document type
        saveDocumentType: builder.mutation({
            query: (data) => ({
                url: data.id ? `${DOCUMENT_TYPES}/${data.id}` : DOCUMENT_TYPES,
                method: data.id ? "PUT" : "POST",
                body: data
            }),
            invalidatesTags: ["DocumentTypes"],
        }),
        // delete document type
        deleteDocumentType: builder.mutation({
            query: (id) => ({
                url: `${DOCUMENT_TYPES}/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["DocumentTypes"],
        }),
        // get banks
        getBanks: builder.query({
            query: () => ({
                url: BANKS,
                method: "GET",
            }),
            providesTags: ["Banks"],
        }),
        // save bank
        saveBank: builder.mutation({
            query: (data) => ({
                url: data.id ? `${BANKS}/${data.id}` : BANKS,
                method: data.id ? "PUT" : "POST",
                body: data
            }),
            invalidatesTags: ["Banks"],
        }),
        // delete bank
        deleteBank: builder.mutation({
            query: (id) => ({
                url: `${BANKS}/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["Banks"],
        }),
        // get holidays
        getHolidays: builder.query({
            query: (year) => ({
                url: year ? `${HOLIDAYS}?year=${year}` : HOLIDAYS,
                method: "GET",
            }),
            providesTags: ["Holidays"],
        }),
        // save holiday
        saveHoliday: builder.mutation({
            query: (data) => ({
                url: data.id ? `${HOLIDAYS}/${data.id}` : HOLIDAYS,
                method: data.id ? "PUT" : "POST",
                body: data
            }),
            invalidatesTags: ["Holidays"],
        }),
        // delete holiday
        deleteHoliday: builder.mutation({
            query: (id) => ({
                url: `${HOLIDAYS}/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["Holidays"],
        }),
        // get roles
        getRoles: builder.query({
            query: () => ({
                url: ROLES,
                method: "GET",
            }),
            providesTags: ["Roles"],
        }),
        // get single role with permissions
        getRole: builder.query({
            query: (id) => ({
                url: `${ROLES}/${id}`,
                method: "GET",
            }),
            providesTags: ["Roles"],
        }),
        // save role
        saveRole: builder.mutation({
            query: (data) => ({
                url: data.id ? `${ROLES}/${data.id}` : ROLES,
                method: data.id ? "PUT" : "POST",
                body: data
            }),
            invalidatesTags: ["Roles"],
        }),
        // delete role
        deleteRole: builder.mutation({
            query: (id) => ({
                url: `${ROLES}/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["Roles"],
        }),
        // get permissions/modules
        getPermissions: builder.query({
            query: () => ({
                url: PERMISSIONS,
                method: "GET",
            }),
            providesTags: ["Permissions"],
        }),
        // update role permissions
        updateRolePermissions: builder.mutation({
            query: ({ roleId, permissions }) => ({
                url: `${ROLES}/${roleId}/permissions`,
                method: "PUT",
                body: { permissions }
            }),
            invalidatesTags: ["Roles"],
        }),
        // get company settings
        getCompanySettings: builder.query({
            query: () => ({
                url: COMPANY_SETTINGS,
                method: "GET",
            }),
            providesTags: ["CompanySettings"],
        }),
        // save company settings
        saveCompanySettings: builder.mutation({
            query: (data) => ({
                url: COMPANY_SETTINGS,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ["CompanySettings"],
        }),
        // get approval settings
        getApprovalSettings: builder.query({
            query: () => ({
                url: APPROVAL_SETTINGS,
                method: "GET",
            }),
            providesTags: ["ApprovalSettings"],
        }),
        // save approval settings
        saveApprovalSettings: builder.mutation({
            query: (data) => ({
                url: APPROVAL_SETTINGS,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ["ApprovalSettings"],
        }),
        // get email settings
        getEmailSettings: builder.query({
            query: () => ({
                url: EMAIL_SETTINGS,
                method: "GET",
            }),
            providesTags: ["EmailSettings"],
        }),
        // save email settings
        saveEmailSettings: builder.mutation({
            query: (data) => ({
                url: EMAIL_SETTINGS,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ["EmailSettings"],
        }),
        // test email
        testEmail: builder.mutation({
            query: (data) => ({
                url: EMAIL_TEST,
                method: "POST",
                body: data
            }),
        }),
        // get localization settings
        getLocalizationSettings: builder.query({
            query: () => ({
                url: LOCALIZATION_SETTINGS,
                method: "GET",
            }),
            providesTags: ["LocalizationSettings"],
        }),
        // save localization settings
        saveLocalizationSettings: builder.mutation({
            query: (data) => ({
                url: LOCALIZATION_SETTINGS,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ["LocalizationSettings"],
        }),
        // get salary settings
        getSalarySettings: builder.query({
            query: () => ({
                url: SALARY_SETTINGS,
                method: "GET",
            }),
            providesTags: ["SalarySettings"],
        }),
        // save salary settings
        saveSalarySettings: builder.mutation({
            query: (data) => ({
                url: SALARY_SETTINGS,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ["SalarySettings"],
        }),
        // get notification settings
        getNotificationSettings: builder.query({
            query: () => ({
                url: NOTIFICATION_SETTINGS,
                method: "GET",
            }),
            providesTags: ["NotificationSettings"],
        }),
        // save notification settings
        saveNotificationSettings: builder.mutation({
            query: (data) => ({
                url: NOTIFICATION_SETTINGS,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ["NotificationSettings"],
        }),
        // get sms settings
        getSmsSettings: builder.query({
            query: () => ({
                url: SMS_SETTINGS,
                method: "GET",
            }),
            providesTags: ["SmsSettings"],
        }),
        // save sms settings
        saveSmsSettings: builder.mutation({
            query: (data) => ({
                url: SMS_SETTINGS,
                method: "PUT",
                body: data
            }),
            invalidatesTags: ["SmsSettings"],
        }),
        // test sms
        testSms: builder.mutation({
            query: (data) => ({
                url: SMS_TEST,
                method: "POST",
                body: data
            }),
        }),
        // delete item
        deleteItem: builder.mutation({
            query: (url) => ({
                url,
                method: "DELETE",
            }),
            invalidatesTags: ["Branches", "LeaveTypes", "Leaves", "Departments", "Designations", "TerminationTypes", "DocumentTypes", "Banks", "Holidays", "Roles"]
        }),
    })
})

export const {
    useGetBranchesQuery,
    useLazyGetBranchesQuery,
    useSaveBranchesMutation,
    useGetDepartmentsQuery,
    useLazyGetDepartmentsQuery,
    useSaveDepartmentsMutation,
    useGetDesignationsQuery,
    useSaveDesignationsMutation,
    useGetLeaveTypesQuery,
    useSaveLeaveTypesMutation,
    useGetTerminationTypesQuery,
    useSaveTerminationTypeMutation,
    useGetCountriesQuery,
    useLazyGetCountriesQuery,
    useGetStatesQuery,
    useLazyGetStatesQuery,
    useGetDocumentTypesQuery,
    useSaveDocumentTypeMutation,
    useDeleteDocumentTypeMutation,
    useGetBanksQuery,
    useSaveBankMutation,
    useDeleteBankMutation,
    useGetHolidaysQuery,
    useSaveHolidayMutation,
    useDeleteHolidayMutation,
    useGetRolesQuery,
    useGetRoleQuery,
    useLazyGetRoleQuery,
    useSaveRoleMutation,
    useDeleteRoleMutation,
    useGetPermissionsQuery,
    useUpdateRolePermissionsMutation,
    useGetCompanySettingsQuery,
    useSaveCompanySettingsMutation,
    useGetApprovalSettingsQuery,
    useSaveApprovalSettingsMutation,
    useGetEmailSettingsQuery,
    useSaveEmailSettingsMutation,
    useTestEmailMutation,
    useGetLocalizationSettingsQuery,
    useSaveLocalizationSettingsMutation,
    useGetSalarySettingsQuery,
    useSaveSalarySettingsMutation,
    useGetNotificationSettingsQuery,
    useSaveNotificationSettingsMutation,
    useGetSmsSettingsQuery,
    useSaveSmsSettingsMutation,
    useTestSmsMutation,
    useDeleteItemMutation
} = settingsApiSlice;